import { useTranslation } from "react-i18next"
import { Icon } from "@phosphor-icons/react"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { Button, ButtonVariant } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogContentProps,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { useConfirmStore } from "@/stores/useConfirmStore"

export type ConfirmationProps = {
  open: boolean
  variant?: DialogContentProps["variant"]
  title?: string
  titleTextClassName?: string
  titleCloseButtonClassName?: string
  content?: React.ReactNode
  cancelText?: string
  confirmText?: string
  isShowConfirmButton?: boolean
  isShowConfirmIcon?: boolean
  confirmIcon?: Icon
  confirmVariant?: ButtonVariant
  isLoading?: boolean
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: () => void
}

const ConfirmationDialog = ({
  open,
  variant = "small",
  title = "common.confirmation.discardUnsavedChanges",
  titleTextClassName,
  titleCloseButtonClassName,
  content = "common.confirmation.unsavedChangesWillBeLostPermanently",
  cancelText = "common.confirmation.keepEditing",
  confirmText = "common.confirmation.discard",
  isShowConfirmButton = true,
  isShowConfirmIcon = true,
  confirmIcon: ConfirmIcon,
  confirmVariant,
  isLoading,
  onClose,
  onCancel,
  onConfirm,
}: ConfirmationProps) => {
  const { t } = useTranslation()
  const cancel = useConfirmStore((state) => state.cancel)

  const handleCancel = () => {
    if (onCancel === undefined) cancel()
    onCancel?.()
  }

  const handleConfirm = () => {
    if (onConfirm === undefined) cancel()
    onConfirm?.()
  }

  const handleClose = () => {
    if (onClose === undefined) cancel()
    onClose?.()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
        <DialogContent
          variant={variant}
          data-testid="confirmation-dialog"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle titleTextClassName={titleTextClassName} closeButtonClassName={titleCloseButtonClassName}>
              <PotentiallyUnsafeInnerHtml className="inline" value={t(title)} />
            </DialogTitle>
            {content && (
              <DialogDescription data-testid="dialog-description" asChild>
                <div>{typeof content === "string" ? t(content) : content}</div>
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={handleCancel} data-testid="dialog-cancel-button">
              {t(cancelText)}
            </Button>
            {isShowConfirmButton && (
              <Button
                variant={confirmVariant ?? "primary"}
                onClick={handleConfirm}
                disabled={isLoading}
                data-testid="dialog-confirm-button">
                {(ConfirmIcon || isShowConfirmIcon) && ConfirmIcon && <ConfirmIcon />}
                {t(confirmText)}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export const Confirmation = () => {
  const confirmation = useConfirmStore((state) => state.confirmation)
  const isOpen = useConfirmStore((state) => state.isOpen)

  return confirmation && <ConfirmationDialog open={isOpen} {...confirmation} />
}
